import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal/Modal'
import MediaPickerRow from '../MediaPickerRow/MediaPickerRow'
import { dataUrlToSquareJpeg } from '../../utils/imageUtils'
import { fetchImageAsDataUrl } from '../../utils/fetchImageAsDataUrl'

export default function IconPickerModal({ isOpen, onClose, onSave, title, loading }) {
  const { t } = useTranslation()
  const fileRef = useRef(null)
  const [preview, setPreview] = useState(null)
  const [urlModalOpen, setUrlModalOpen] = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [urlError, setUrlError] = useState('')
  const [busy, setBusy] = useState(false)

  const effectiveTitle = title || t('groups.iconPicker.title')

  const processDataUrl = async (dataUrl) => {
    setBusy(true)
    try {
      const jpeg = await dataUrlToSquareJpeg(dataUrl, 128, 0.85)
      setPreview(jpeg)
    } catch {
      alert(t('groups.iconPicker.processError'))
    } finally {
      setBusy(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => processDataUrl(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleUrlLoad = async () => {
    if (!urlInput.trim()) return
    setUrlError('')
    setBusy(true)
    try {
      const dataUrl = await fetchImageAsDataUrl(urlInput.trim())
      await processDataUrl(dataUrl)
      setUrlModalOpen(false)
      setUrlInput('')
    } catch {
      setUrlError(t('groups.iconPicker.urlError'))
    } finally {
      setBusy(false)
    }
  }

  const handleSave = () => {
    if (!preview) return
    onSave(preview)
  }

  const handleClose = () => {
    setPreview(null)
    setUrlInput('')
    setUrlError('')
    onClose()
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title={effectiveTitle}>
        <div className="space-y-4">
          <MediaPickerRow
            hint={t('groups.iconPicker.hint')}
            previewSrc={preview}
            emptyLabel={t('groups.iconPicker.preview')}
            busy={busy}
            fileInputRef={fileRef}
            accept="image/*"
            onFileChange={handleFileChange}
            onOpenUrlModal={() => setUrlModalOpen(true)}
            previewClassName="w-20 h-20 rounded-lg"
            previewImgClassName="object-cover"
          />

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-ghost" onClick={handleClose} disabled={loading}>
              {t('common.buttons.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading || !preview}
            >
              {loading ? t('groups.modal.saving') : t('common.buttons.save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Sub-modal de URL */}
      <Modal isOpen={urlModalOpen} onClose={() => setUrlModalOpen(false)} title={t('groups.iconPicker.urlModalTitle')}>
        <div className="space-y-3">
          <input
            type="url"
            className={`input w-full ${urlError ? 'border-red-400' : ''}`}
            placeholder="https://..."
            value={urlInput}
            onChange={(e) => { setUrlInput(e.target.value); setUrlError('') }}
            onKeyDown={(e) => e.key === 'Enter' && handleUrlLoad()}
            autoFocus
          />
          {urlError && <p className="text-xs text-red-500">{urlError}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => setUrlModalOpen(false)}>{t('common.buttons.cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={handleUrlLoad} disabled={busy}>
              {busy ? t('groups.iconPicker.loading') : t('groups.iconPicker.load')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
